import { DateOfBirth } from '@fhs/auth/src/types';
import { SimplyOfferEvents } from '@fhs/loyalty';
import { ServiceMode } from '@rbi-ctg/menu';
import {
  AllowedEvent as AllowedEventMParticle,
  EventTypes,
  IEventBase,
  Restaurant,
} from '@rbilabs/mparticle-client';
import { AuthIdentifiers, AuthV2ChallengeType } from 'generated/rbi-graphql';
import { REGIONS } from 'state/intl/types';
import { DeliveryFees } from 'state/service-mode/types';

import {
  CustomEventNames,
  DialogViewComponentNames,
  ErrorDialogViewComponentNames,
} from './constants';

interface IUpdateUserAttributes extends IEventBase {
  name: CustomEventNames.UPDATE_USER_ATTRIBUTES;
  type: EventTypes.UserContent;
}

interface ISaveNewPaymentMethod extends IEventBase {
  name: CustomEventNames.SAVE_NEW_PAYMENT_METHOD;
  type: EventTypes.UserContent;
}

interface IPageViewEvent extends IEventBase {
  quotedFeeCents?: number;
}

interface ISupportFormSubmitted extends IEventBase {
  name: CustomEventNames.SUPPORT_FORM_SUBMITTED;
  type: EventTypes.UserContent;
  attributes: {
    caseId: string;
    userEmail: string;
    orderDateTime?: string | undefined;
    userAccountRegion?: string;
    userRegion: REGIONS;
  };
}

interface IPypestreamSupportOpen extends IEventBase {
  name: CustomEventNames.PYPESTREAM_SUPPORT_OPEN;
  type: EventTypes.UserContent;
}

interface IStoreLocatorTabView extends IEventBase {
  name: CustomEventNames.STORE_LOCATOR_TAB_VIEW;
  type: EventTypes.Other;
}

export interface IStoreSelectedEventWithCache extends IEventBase {
  name: 'Store Selected';
  type: EventTypes.Other;
  attributes: Restaurant & { isCacheEnabled: boolean };
}

export interface IOffersListed extends IEventBase {
  name: CustomEventNames.OFFERS_LISTED;
  type: EventTypes.UserContent;
  attributes: {
    version: string;
    offers: { id: string; name: string; cmsId?: string }[];
  };
}

export interface IHomepageReorder extends IEventBase {
  name: CustomEventNames.HOMEPAGE_REORDER;
  type: EventTypes.Other;
}

export interface IChangeServiceMode extends IEventBase {
  name: CustomEventNames.SELECT_SERVICE_MODE;
  type: EventTypes.Other;
  attributes: IChangeServiceModeAttributes;
}

export type IChangeServiceModeAttributes = {
  oldServiceMode: ServiceMode;
  newServiceMode: ServiceMode;
};

export interface IDeliveryFeesSectionDisplayed extends IEventBase {
  name: CustomEventNames.DELIVERY_FEES_SECTION_DISPLAYED;
  type: EventTypes.Other;
  attributes: IDeliveryFeesSectionDisplayedAttributes;
}

export type IDeliveryFeesSectionDisplayedAttributes = {
  breakdownFeesEnabled?: boolean;
  donationCents?: number;
  fees?: DeliveryFees;
  taxCents?: number;
  tipCents?: number;
};

export type ICreateOtpEventAttributes = {
  identifierType: AuthIdentifiers;
};

export interface ICreateOtpEvent extends IEventBase {
  name: CustomEventNames.CREATE_OTP_ATTEMPT;
  type: EventTypes.Other;
  attributes: ICreateOtpEventAttributes;
}

export type ILoginAttemptEventAttributes = {
  method: 'signIn' | 'signUp';
};

export interface ILoginAttemptEvent extends IEventBase {
  name: CustomEventNames.LOGIN;
  type: EventTypes.Other;
  attributes: ILoginAttemptEventAttributes;
}

export type IResendOtpEventAttributes = {};

export interface IResendOtpEvent extends IEventBase {
  name: CustomEventNames.RESEND_OTP_ATTEMPT;
  type: EventTypes.Other;
  attributes: IResendOtpEventAttributes;
}

export type IValidateOtpEventAttributes = {
  successful: boolean;
  challengeType: AuthV2ChallengeType;
  numAccounts?: number;
};

export interface IValidateOtpEvent extends IEventBase {
  name: CustomEventNames.RESEND_OTP_ATTEMPT;
  type: EventTypes.Other;
  attributes: IValidateOtpEventAttributes;
}

export type ISignUpEventAttributes = {
  dob: DateOfBirth;
};

export interface ISignUpEvent extends IEventBase {
  name: CustomEventNames.SIGN_UP_SUCCESSFUL;
  type: EventTypes.Other;
  attributes: ISignUpEventAttributes;
}

export interface IAuthV2StepEvent extends IEventBase {
  name: CustomEventNames.AUTHENTICATION_STEP;
  type: EventTypes.Other;
  attributes:
    | { step: CustomEventNames } & (
        | ICreateOtpEventAttributes
        | ILoginAttemptEventAttributes
        | IResendOtpEventAttributes
        | ISignUpEventAttributes
        | IValidateOtpEventAttributes
      );
}

export interface IDeliveryStoreAssigned extends IEventBase {
  name: CustomEventNames.DELIVERY_STORE_ASSIGNED;
  type: EventTypes.Other;
  attributes: IDeliveryStoreAssignedAttributes;
}

export type IDeliveryStoreAssignedAttributes = {
  quotedFeeCents: number;
} & Restaurant;

export interface IDiscountNotApplied extends IEventBase {
  name: CustomEventNames.DISCOUNT_NOT_APPLIED;
  type: EventTypes.Other;
  attributes: {
    offer: { id: string; loyaltyEngineId: string; name: string };
  };
}

export interface IScheduleFutureOrderAttributes extends Record<string, any> {
  restaurantId: string | null;
  storeId: string | null;
  isRestaurantClosed: boolean;
  restaurantAddress: string | null;
  restaurantZip: string | null;
  restaurantCity: string | null;
  restaurantState: string | null;
  restaurantCountry: string | null;
}

export interface IScheduleFutureOrder extends IEventBase {
  name: CustomEventNames.SCHEDULE_FUTURE_ORDER;
  type: EventTypes.Other;
  attributes: IScheduleFutureOrderAttributes;
}

export interface IChangeRestaurantAttributes extends Record<string, any> {
  currentRestaurantId: string | null;
  currentStoreId: string | null;
  isCurrentRestaurantClosed: boolean;
  restaurantAddress: string | null;
  restaurantZip: string | null;
  restaurantCity: string | null;
  restaurantState: string | null;
  restaurantCountry: string | null;
}

export interface IChangeRestaurant extends IEventBase {
  name: CustomEventNames.CHANGE_RESTAURANT;
  type: EventTypes.Other;
  attributes: IChangeRestaurantAttributes;
}

export interface IErrorDialogView extends IEventBase {
  name: CustomEventNames.ERROR_DIALOG_VIEW;
  type: EventTypes.Other;
  attributes: {
    component: ErrorDialogViewComponentNames;
    errorMessage: string | string[];
  };
}

interface IDialogView extends IEventBase {
  name: CustomEventNames.DIALOG_VIEW;
  type: EventTypes.Other;
  attributes: {
    component: DialogViewComponentNames;
  };
}

interface IInlineAlertDisplayed extends IEventBase {
  name: CustomEventNames.INLINE_ALERT_DISPLAYED;
  type: EventTypes.Other;
  attributes: {
    inlineAlertHeader: string;
    inlineAlertType: string;
  };
}

export type AllowedEvent =
  | AllowedEventMParticle
  | IUpdateUserAttributes
  | ISaveNewPaymentMethod
  | ISupportFormSubmitted
  | IPypestreamSupportOpen
  | IStoreSelectedEventWithCache
  | IStoreLocatorTabView
  | IOffersListed
  | IHomepageReorder
  | IChangeServiceMode
  | IDeliveryFeesSectionDisplayed
  | IDiscountNotApplied
  | IErrorDialogView
  | IScheduleFutureOrder
  | IChangeRestaurant
  | IDialogView
  | IPageViewEvent
  | SimplyOfferEvents
  | IInlineAlertDisplayed
  | ICustomizationLimitExceeded
  | IStoreClosedOnOperationalHours;
export interface ILogRBIEvent {
  (event: AllowedEvent): void;
}

export enum SignInPhases {
  START = 'start',
  COMPLETE = 'complete',
}

/**
 * Used for attribution tracking
 */
export interface IUtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface IUtmParamsAttributes {
  'UTM Source'?: string;
  'UTM Medium'?: string;
  'UTM Campaign'?: string;
  'UTM Term'?: string;
  'UTM Content'?: string;
}

export interface ICRMEventsUniversalAttributes extends IUtmParamsAttributes {
  'Service Mode': string;
  'Pickup Mode': string;
  isSmallScreen: boolean;
  isMobileWeb: boolean;
  currentBuild: string;
}

export interface ICRMEventsAttributes extends IUtmParamsAttributes {
  brand?: string;
  region?: string;
  env?: string;
  'Date of Birth'?: string;
  'Email Opt In'?: string;
  ccToken?: string;
  favoriteStores?: string;
  language?: string;
  Locale?: string;
  'Legacy User'?: string;
  'RBI Cognito ID'?: string;
  'IOS Location Permissions'?: string;
  'Android Location Permissions'?: string;
}

export interface IFeatureFlags {
  isSimplyBetterMenuListViewEnabled: boolean;
  isSimplyBetterMenuDetailViewEnabled: boolean;
  isSimplyBetterOffersUIEnabled: boolean;
  isExplicitDonationEnabled: boolean;
}

export interface ICustomizationLimitExceeded extends IEventBase {
  name: CustomEventNames.CUSTOMIZATION_LIMIT_EXCEEDED;
  type: EventTypes.Other;
  attributes: {
    pickerSlug: string;
    selectedPickerAspectOptionId?: string;
    customizationDisplayGroupDisplayName: string;
    targetCustomizationOptionDisplayName: string;
    maxAllowedCustomizations: number;
    selectedCustomizationsInDisplayGroup: Array<string>;
  };
}

export interface IStoreClosedOnOperationalHours extends IEventBase {
  name: CustomEventNames.STORE_CLOSED_ON_OPERATIONAL_HOURS;
  type: EventTypes.Other;
  attributes: {
    storeId: string;
    isAvailable: boolean;
    currentHour: number;
    dateNow: Date;
  };
}
